import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import './Home.css';
import JourneySection from '../../Components/JourneySection/JourneySection';
// Placeholder for client logos - you can replace these with actual image imports
import LogoPlaceholder from '../../Images/LogoBlank.png';

const Home = ({ openModal }) => {
  // Schema markup for the new brand
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ByteSand",
    "description": "Your trusted partner in becoming an AI-first company.",
    "url": "https://bytesand.ai",
    "logo": "https://bytesand.ai/images/social/square-logo-400x400.png", // Reverted to original
  };

  const clients = [
    { name: "Stockless", logo: LogoPlaceholder },
    { name: "Asmuss", logo: LogoPlaceholder },
    { name: "RealEstateU", logo: LogoPlaceholder },
    { name: "DentCPS", logo: LogoPlaceholder },
    { name: "Citation", logo: LogoPlaceholder },
    { name: "ReadyRNs", logo: LogoPlaceholder },
    { name: "ShopSolar", logo: LogoPlaceholder },
  ];

  // Animation variant for sections
  const slideInVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const MotionSection = ({ children, className, id }) => (
    <motion.section
      id={id}
      className={`slide ${className}`}
      variants={slideInVariant}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      {children}
    </motion.section>
  );

  return (
    <>
      <Helmet>
        <title>ByteSand | AI Transformation</title>
        <meta 
          name="description" 
          content="We are your trusted partner in becoming an AI-first company. We help you identify opportunities, educate your team, and develop custom AI solutions."
        />
        <link rel="canonical" href="https://bytesand.ai" />

        {/* Open Graph meta tags */}
        <meta property="og:title" content="ByteSand | AI Transformation" />
        <meta property="og:description" content="Your trusted partner in becoming an AI-first company." />
        <meta property="og:url" content="https://bytesand.ai" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ByteSand" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ByteSand | AI Transformation" />
        <meta name="twitter:description" content="Your trusted partner in becoming an AI-first company." />

        {/* Schema markup */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <div className="home-container">
        {/* Slide 1: Hero */}
        <MotionSection id="hero" className="hero-slide">
            <p className="hero-statement">We are not just another AI Consultancy.</p>
            <h1>We are your <span className="highlight-text">AI Transformation</span> Partner.</h1>
            <p className="hero-subtitle">We don't just advise. We identify, educate, and develop AI solutions that become the core of your business.</p>
        </MotionSection>

        {/* Slide 2: Mission */}
        <MotionSection className="mission-slide">
            <h2>We put AI at the <span className="highlight-text">center</span> of everything we do.</h2>
            <p>For us, AI is not an afterthought; it's the baseline. We build systems that drive efficiency, unlock insights, and create tangible value from day one.</p>
        </MotionSection>
        
        {/* Slide 3: Journey Intro */}
        <MotionSection className="journey-intro-slide">
            <h2>We guide companies through our proven 3-step AI Transformation Journey.</h2>
        </MotionSection>

        <JourneySection />

        {/* Slide 4: Results */}
        <MotionSection id="results" className="results-slide">
            <h2>We don't sell AI. We sell <span className="highlight-text">Results.</span></h2>
            <div className="results-grid">
                <div className="result-item">
                    <span className="result-number">80+</span>
                    <p>Workflows automated</p>
                </div>
                <div className="result-item">
                    <span className="result-number">150+</span>
                    <p>AI Opportunities identified</p>
                </div>
                <div className="result-item">
                    <span className="result-number">50+</span>
                    <p>Bespoke AI solutions developed</p>
                </div>
            </div>
        </MotionSection>

        {/* Slide 5: Clients */}
        <MotionSection className="clients-slide">
            <h2>Trusted by industry leaders and innovators.</h2>

        </MotionSection>
        
        {/* Slide 6: Final CTA */}
        <MotionSection id="partner" className="partner-slide">
            <h2>The best AI systems are built <span className="highlight-text" style={{ whiteSpace: 'nowrap' }}>side by side.</span></h2>
            <p>Let's Partner Up.</p>
            <button className="cta-button" onClick={openModal}>
              Get In Touch
            </button>
        </MotionSection>
      </div>
    </>
  );
};

export default Home; 